import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Crosshair, ExternalLink, FolderKanban, Info, Lock, Maximize2, Minus, Plus, RefreshCw, Search, Spline, X } from 'lucide-react';
import GraphCanvas from './GraphCanvas';
import Tooltip from '../ui/Tooltip';
import ContextMenu from '../ui/ContextMenu';
import GraphDetails, { TypeBadge, parentPath } from './GraphDetails';
import SearchHighlight from './SearchHighlight';
import OsScroll from '../ui/OsScroll';
import SshHostChoiceModal from './SshHostChoiceModal';
import ProjectPickerModal from './ProjectPickerModal';
import GraphScopePicker, { useScopeLabel } from './GraphScopePicker';
import { TYPE_STYLE, isDarkTheme, nodeColor } from './graphTheme';
import { useLinkGraph } from '../../graph/useLinkGraph';
import { useGraphUiStore, bumpGraph } from '../../graph/graphUiStore';
import { filterByScope, projectSubgraph, searchNodes } from '../../graph/graphQueries';
import { setGraphProject, useGraphProjectScope, withoutProjects } from '../../graph/graphProjectScope';
import { EXPLICIT_RELATIONS } from '../../graph/graphModel';
import {
  addLink,
  addToProject,
  canWriteScope,
  projectAccepts,
  removeFromProject,
  removeLink,
  scopeKeyOfWire,
} from '../../graph/projectsStore';
import { openGraphNode } from '../../graph/graphNavigation';
import { useFtpStore } from '../../store/ftpStore';
import { useTeamsStore } from '../../store/teamsStore';
import '../Graph.css';

const DEFAULT_HIDDEN = new Set();
// Up to this many projects are listed in the right-click menu; above it the
// menu offers "Add to project…" and the picker modal.
export const MENU_PROJECT_LIMIT = 3;
// Rows in the search dropdown; the canvas still highlights every match.
const SEARCH_LIST_LIMIT = 50;

/**
 * Graph page (rail icon "Graph"): everything the user has, and how it connects.
 * Full-bleed like Calendar — no sidebar column.
 */
export default function GraphView() {
  const { t } = useTranslation();
  const { graph, index, projects, unavailable, loading, refresh } = useLinkGraph();
  const canvasRef = useRef(null);
  const searchRef = useRef(null);

  // The toolbar project (card 6.2): one scope for every panel.
  const projectScope = useGraphProjectScope();
  const projectFilter = projectScope.mode === 'project' ? projectScope.projectId : null;
  const focusNodeId = useGraphUiStore((s) => s.focusNodeId);
  const clearFocus = useGraphUiStore((s) => s.clearFocus);
  const storedScope = useGraphUiStore((s) => s.scopeFilter);
  const setScopeFilter = useGraphUiStore((s) => s.setScopeFilter);
  const teams = useTeamsStore((s) => s.teams);
  // A team the user has left is no filter at all (like a vanished project).
  const scopeFilter =
    storedScope && storedScope.startsWith('team:') && !(teams || []).some((x) => x?.id != null && `team:${x.id}` === storedScope)
      ? null
      : storedScope;
  const scopeLabel = useScopeLabel();

  const [hiddenTypes, setHiddenTypes] = useState(DEFAULT_HIDDEN);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchActive, setSearchActive] = useState(0);
  const [pendingFocus, setPendingFocus] = useState(null); // id revealed by clearing a filter
  const searchListRef = useRef(null);
  const searchHoverRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [menu, setMenu] = useState(null); // { node, x, y } — right-click menu
  const [linkFrom, setLinkFrom] = useState(null); // node while drawing a link
  const [relationMenu, setRelationMenu] = useState(null); // { from, to, x, y }
  const [projectPickerFor, setProjectPickerFor] = useState(null); // node
  const [error, setError] = useState(null);
  const unlocking = useFtpStore((s) => s.unlocking);
  const unlockVault = useCallback(async () => {
    const ok = await useFtpStore.getState().unlockVault();
    if (ok) refresh();
  }, [refresh]);

  const run = useCallback(async (fn) => {
    setError(null);
    try {
      await fn();
      bumpGraph();
    } catch (err) {
      // Team-project refusals carry a code (projectsStore): say it in the UI language.
      setError(err?.code ? t(`projects.error.${err.code}`) : err?.message || String(err));
    }
  }, [t]);

  useEffect(() => {
    if (!error) return undefined;
    const id = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(id);
  }, [error]);

  // ── what is drawn ─────────────────────────────────────────────────────────
  const visible = useMemo(() => {
    let nodes = graph.nodes;
    let edges = graph.edges;
    if (projectScope.mode === 'pending') return { version: graph.version, nodes: [], edges: [] };
    if (projectFilter) {
      const sub = projectSubgraph(index, `project:${projectFilter}`, { cap: 5000 });
      nodes = sub ? sub.nodes : [];
      edges = sub ? sub.edges : [];
    }
    if (projectScope.mode === 'none') {
      const loose = withoutProjects(nodes, edges);
      nodes = loose.nodes;
      edges = loose.edges;
    }
    if (scopeFilter) {
      const inScope = filterByScope(nodes, edges, scopeFilter);
      nodes = inScope.nodes;
      edges = inScope.edges;
    }
    if (hiddenTypes.size) {
      nodes = nodes.filter((n) => !hiddenTypes.has(n.type));
      const keep = new Set(nodes.map((n) => n.id));
      edges = edges.filter((e) => keep.has(e.from) && keep.has(e.to));
    }
    if (nodes === graph.nodes && edges === graph.edges) return graph;
    return { version: graph.version, nodes, edges };
  }, [graph, index, projectScope.mode, projectFilter, scopeFilter, hiddenTypes]);

  const searchResults = useMemo(() => (query.trim() ? searchNodes(index, query, { limit: 100 }) : []), [index, query]);
  const matches = useMemo(() => (query.trim() ? new Set(searchResults.map((n) => n.id)) : null), [query, searchResults]);
  const drawnIds = useMemo(() => new Set(visible.nodes.map((n) => n.id)), [visible]);
  const searchList = searchResults.slice(0, SEARCH_LIST_LIMIT);
  const showSearchList = searchOpen && !!query.trim();

  const knownIds = useMemo(() => new Set(graph.nodes.map((n) => n.id)), [graph]);

  const counts = useMemo(() => {
    const c = {};
    for (const n of graph.nodes) c[n.type] = (c[n.type] || 0) + 1;
    return c;
  }, [graph]);

  // Ctrl/Cmd+F focuses "Find an item…" (owner request) instead of the
  // WebView's own find bar, which cannot search a canvas anyway.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── focus requests from other pages ───────────────────────────────────────
  useEffect(() => {
    if (!focusNodeId || !index.byId.has(focusNodeId)) return;
    setSelectedId(focusNodeId);
    const id = focusNodeId;
    clearFocus();
    // Give the canvas a frame to receive the graph first.
    requestAnimationFrame(() => canvasRef.current?.focusNode(id));
  }, [focusNodeId, index, clearFocus]);

  useEffect(() => {
    if (selectedId && !index.byId.has(selectedId)) setSelectedId(null);
  }, [index, selectedId]);

  const toggleType = (type) =>
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });

  // ── search dropdown (owner: like the Project search — a live list; picking a
  // row flies the canvas to that node, so a far-away match is easy to reach) ──
  const hoverFromSearch = useCallback((id) => {
    if (searchHoverRef.current === id) return;
    searchHoverRef.current = id;
    canvasRef.current?.hoverNode(id);
  }, []);

  useEffect(() => {
    const node = showSearchList ? searchList[searchActive] : null;
    hoverFromSearch(node && drawnIds.has(node.id) ? node.id : null);
  }, [showSearchList, searchList, searchActive, drawnIds, hoverFromSearch]);

  useEffect(() => {
    searchListRef.current?.querySelector('.pj-result--active')?.scrollIntoView?.({ block: 'nearest' });
  }, [searchActive]);

  // Owner: centre the camera on a row while navigating (↑ ↓ or mouse over),
  // before any click. Only on navigation — not on every keystroke while typing —
  // and debounced so sweeping the pointer over the list does not jerk the view.
  const previewTimer = useRef(0);
  const previewRow = useCallback(
    (i) => {
      setSearchActive(i);
      clearTimeout(previewTimer.current);
      const node = searchList[i];
      if (!node || !drawnIds.has(node.id)) return;
      previewTimer.current = setTimeout(() => canvasRef.current?.focusNode(node.id), 90);
    },
    [searchList, drawnIds]
  );
  useEffect(() => () => clearTimeout(previewTimer.current), []);

  // Owner: when typing narrows the list to a single match, centre on it
  // straight away (still only a preview — nothing is selected until picked).
  const singleMatchId = showSearchList && searchResults.length === 1 ? searchResults[0].id : null;
  const lastSingleRef = useRef(null);
  useEffect(() => {
    if (singleMatchId === lastSingleRef.current) return;
    lastSingleRef.current = singleMatchId;
    if (singleMatchId && drawnIds.has(singleMatchId)) previewRow(0);
  }, [singleMatchId, drawnIds, previewRow]);

  const goToNode = (node) => {
    if (!node) return;
    clearTimeout(previewTimer.current);
    setSearchOpen(false);
    hoverFromSearch(null);
    if (drawnIds.has(node.id)) {
      setSelectedId(node.id);
      canvasRef.current?.focusNode(node.id);
      return;
    }
    // Hidden by a filter: lift just what hides it, then focus once it is drawn.
    if (hiddenTypes.has(node.type)) toggleType(node.type);
    if (projectFilter) {
      const sub = projectSubgraph(index, `project:${projectFilter}`, { cap: 5000 });
      if (!sub?.nodes.some((n) => n.id === node.id)) setGraphProject(null);
    } else if (projectScope.mode !== 'all') {
      setGraphProject(null);
    }
    if (scopeFilter && node.scope !== scopeFilter) setScopeFilter(null);
    setPendingFocus(node.id);
  };

  useEffect(() => {
    if (!pendingFocus || !drawnIds.has(pendingFocus)) return;
    const id = pendingFocus;
    setPendingFocus(null);
    setSelectedId(id);
    requestAnimationFrame(() => canvasRef.current?.focusNode(id));
  }, [pendingFocus, drawnIds]);

  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!searchList.length) return;
      e.preventDefault();
      if (!searchOpen) {
        setSearchOpen(true);
        return;
      }
      previewRow(e.key === 'ArrowDown' ? Math.min(searchList.length - 1, searchActive + 1) : Math.max(0, searchActive - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      goToNode(searchList[searchActive]);
    } else if (e.key === 'Escape') {
      if (showSearchList) setSearchOpen(false);
      else setQuery('');
    }
  };

  const onSelect = useCallback((node) => setSelectedId(node ? node.id : null), []);
  const onOpen = useCallback((node) => openGraphNode(node, index), [index]);
  const onContextMenu = useCallback(({ node, clientX, clientY }) => setMenu({ node, x: clientX, y: clientY }), []);
  const onLinkDrop = useCallback(({ from, to, clientX, clientY }) => {
    if (from.type === 'project' && to.type === 'project') {
      setError(t('graph.link.projectInProject'));
      return;
    }
    setRelationMenu({ from, to, x: clientX, y: clientY });
  }, [t]);
  const startLink = useCallback((node, point) => canvasRef.current?.startLink(node.id, point), []);
  const center = useCallback((id) => canvasRef.current?.focusNode(id), []);

  const memberOfProject = useCallback(
    (nodeId, projectId) =>
      (index.adj.get(nodeId) || []).some(({ edge }) => edge.rel === 'contains' && edge.from === `project:${projectId}` && edge.to === nodeId),
    [index]
  );
  // Unknown id → null, and callers offer/do nothing: a bare id would be read
  // as a PERSONAL project and put a team project's membership in the wrong scope.
  const projectById = useCallback((id) => projects.find((p) => p.id === id) || null, [projects]);
  const withProject = useCallback((id, fn) => {
    const project = projectById(id);
    if (project) run(() => fn(project));
  }, [projectById, run]);
  // The projects an item's menus offer: ones it may join, and ones it is in
  // that the user may change (a team project's viewer gets neither).
  const projectsFor = useCallback(
    (node) => projects.filter((p) => (memberOfProject(node.id, p.id) ? canWriteScope(p.scopeKey) : projectAccepts(p, node))),
    [projects, memberOfProject]
  );

  const menuItems = useMemo(() => {
    if (!menu) return [];
    if (!menu.node) {
      return [
        { icon: <Maximize2 size={14} />, label: t('graph.fit'), onClick: () => canvasRef.current?.fitView(null) },
        { icon: <RefreshCw size={14} />, label: t('graph.refresh'), onClick: refresh },
      ];
    }
    const node = menu.node;
    const items = [
      { icon: <ExternalLink size={14} />, label: t('graph.open'), onClick: () => openGraphNode(node, index) },
      { icon: <Info size={14} />, label: t('graph.menu.details'), onClick: () => setSelectedId(node.id) },
      { icon: <Crosshair size={14} />, label: t('graph.center'), onClick: () => center(node.id) },
      { icon: <Spline size={14} />, label: t('graph.menu.linkTo'), onClick: () => startLink(node, { x: menu.x, y: menu.y }) },
    ];
    if (node.type !== 'project') {
      const offered = projectsFor(node);
      // Owner: with many projects the menu shows ONE "Add to project…" that
      // opens a filterable picker, instead of a line per project.
      if (offered.length > MENU_PROJECT_LIMIT) {
        items.push({ icon: <FolderKanban size={14} />, label: t('graph.details.addToProject'), onClick: () => setProjectPickerFor(node) });
      }
      for (const p of offered.length > MENU_PROJECT_LIMIT ? [] : offered) {
        const inIt = memberOfProject(node.id, p.id);
        items.push({
          icon: inIt ? <Check size={14} /> : <FolderKanban size={14} />,
          label: inIt ? t('graph.menu.removeFrom', { name: p.name }) : t('graph.menu.addTo', { name: p.name }),
          onClick: () => run(() => (inIt ? removeFromProject(p, node.id) : addToProject(p, node))),
        });
      }
    }
    return items;
  }, [menu, t, refresh, index, center, startLink, projectsFor, memberOfProject, run]);

  const relationItems = useMemo(() => {
    if (!relationMenu) return [];
    const { from, to } = relationMenu;
    // A project cannot contain a project: offer nothing that would only fail.
    if (from.type === 'project' && to.type === 'project') return [];
    const relations = from.type === 'project' || to.type === 'project' ? [] : EXPLICIT_RELATIONS;
    const items = relations.map((rel) => ({
      label: t(`graph.rel.${rel}.out`),
      onClick: () => run(() => addLink({ from: from.id, to: to.id, rel })),
    }));
    // Membership goes through addToProject, so a team project's own scope and
    // its checks apply — never a personal "contains" link to a team project.
    const fromProject = from.type === 'project' ? projectById(from.id.slice('project:'.length)) : null;
    if (fromProject && projectAccepts(fromProject, to)) {
      items.push({ label: t('graph.rel.contains.out'), onClick: () => run(() => addToProject(fromProject, to)) });
    }
    const toProject = to.type === 'project' ? projectById(to.id.slice('project:'.length)) : null;
    if (toProject && projectAccepts(toProject, from)) {
      // Dropping an item on a project means "put it in the project".
      items.push({ icon: <FolderKanban size={14} />, label: t('graph.menu.addTo', { name: to.label }), onClick: () => run(() => addToProject(toProject, from)) });
    }
    return items;
  }, [relationMenu, t, run, projectById]);

  const selected = selectedId ? index.byId.get(selectedId) : null;
  // `teams` (signed in, empty roster) is honest over MCP but not on the page:
  // there is no "teams loaded" flag, and for every user without a team (Free,
  // Pro, or simply none) the roster is empty for good — a "still loading"
  // notice would never go away (review R-B1). The vault has its own chip.
  const pageNotices = unavailable.filter((source) => source !== 'vault' && source !== 'teams');
  const typesPresent = Object.keys(TYPE_STYLE).filter((type) => counts[type]);

  return (
    <div className="graph-view" data-testid="graph-view">
      {/* Owner: project dropdown on the left, search in the middle, view actions right. */}
      <div className="graph-toolbar graph-toolbar--three">
        <div className="graph-toolbar-left">
          {/* The project is the title bar's switcher only (owner, 20.10.2026: the
              Graph's own project dropdown read as a duplicate of it). */}
          <GraphScopePicker value={scopeFilter} onChange={setScopeFilter} />
        </div>
        <div className="graph-search">
          <Search size={14} />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSearchActive(0);
              setSearchOpen(true);
            }}
            onFocus={() => {
              setSearchActive(0);
              setSearchOpen(true);
            }}
            onBlur={() => setSearchOpen(false)}
            onKeyDown={onSearchKey}
            placeholder={t('graph.searchPlaceholder')}
            aria-label={t('graph.searchPlaceholder')}
            role="combobox"
            aria-expanded={showSearchList}
            aria-controls="graph-search-results"
            autoComplete="off"
          />
          {query && (
            <button type="button" className="graph-icon-btn" onClick={() => setQuery('')} aria-label={t('common.close')}>
              <X size={12} />
            </button>
          )}
          {showSearchList && (
            // mousedown is swallowed so a click lands before the input's blur closes the list.
            <div className="graph-search-results" id="graph-search-results" onMouseDown={(e) => e.preventDefault()} onMouseLeave={() => hoverFromSearch(null)}>
              {searchList.length === 0 ? (
                <div className="pj-results-empty">{t('projects.noResults')}</div>
              ) : (
                <>
                  <OsScroll className="graph-search-scroll">
                    <ul className="pj-results-list" ref={searchListRef} role="listbox">
                      {searchList.map((node, i) => {
                        const path = parentPath(index, node.id);
                        const hidden = !drawnIds.has(node.id);
                        const where = scopeLabel(node);
                        return (
                          <li key={node.id}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={i === searchActive}
                              className={`pj-result${i === searchActive ? ' pj-result--active' : ''}${hidden ? ' graph-search-row--hidden' : ''}`}
                              onMouseEnter={() => previewRow(i)}
                              onClick={() => goToNode(node)}
                            >
                              <TypeBadge type={node.type} size="sm" />
                              <span className="pj-result-text">
                                <span className="pj-result-title">
                                  {node.method && <span className="pj-method">{node.method}</span>}
                                  <SearchHighlight text={node.label} query={query} />
                                </span>
                                <span className="pj-result-meta">
                                  {t(TYPE_STYLE[node.type]?.labelKey || 'graph.type.item')}
                                  {where && ` · ${where}`}
                                  {path.length > 0 && ` · ${path.join(' › ')}`}
                                  {hidden && ` · ${t('graph.search.hiddenByFilter')}`}
                                </span>
                              </span>
                              <span className="pj-result-action">
                                <Crosshair size={13} aria-hidden /> {t('graph.search.show')}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </OsScroll>
                  <div className="pj-results-foot">
                    {searchResults.length > searchList.length
                      ? t('graph.search.moreKeys', { shown: searchList.length, total: searchResults.length })
                      : t('graph.search.keys')}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="graph-toolbar-end">
          <span className="graph-stats">
            {t('graph.stats', { nodes: visible.nodes.length, edges: visible.edges.length })}
          </span>
          {/* The app's own tooltip, not the browser's title bubble (owner). */}
          <div className="graph-toolbar-right">
            <Tooltip content={t('graph.zoomOut')} side="bottom">
              <button type="button" className="graph-btn graph-btn--icon" onClick={() => canvasRef.current?.zoomBy(1 / 1.4)} aria-label={t('graph.zoomOut')}>
                <Minus size={14} />
              </button>
            </Tooltip>
            <Tooltip content={t('graph.zoomIn')} side="bottom">
              <button type="button" className="graph-btn graph-btn--icon" onClick={() => canvasRef.current?.zoomBy(1.4)} aria-label={t('graph.zoomIn')}>
                <Plus size={14} />
              </button>
            </Tooltip>
            <Tooltip content={t('graph.fit')} side="bottom">
              <button type="button" className="graph-btn graph-btn--icon" onClick={() => canvasRef.current?.fitView(matches)} aria-label={t('graph.fit')}>
                <Maximize2 size={14} />
              </button>
            </Tooltip>
            <Tooltip content={t('graph.refresh')} side="bottom">
              <button type="button" className="graph-btn graph-btn--icon" onClick={refresh} aria-label={t('graph.refresh')}>
                <RefreshCw size={14} />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="graph-body">
        <div className="graph-stage">
          <GraphCanvas
            ref={canvasRef}
            graph={visible}
            knownIds={knownIds}
            selectedId={selectedId}
            highlightIds={matches}
            onSelect={onSelect}
            onOpen={onOpen}
            onContextMenu={onContextMenu}
            onLinkDrop={onLinkDrop}
            onLinkModeChange={setLinkFrom}
          />

          {linkFrom && (
            <div className="graph-linkbar" role="status">
              <Spline size={13} aria-hidden />
              <span>{t('graph.link.hint', { name: linkFrom.label })}</span>
              <button type="button" className="graph-linkbar-cancel" onClick={() => canvasRef.current?.cancelLink()}>
                {t('common.cancel')}
              </button>
            </div>
          )}
          {error && <div className="graph-toast" role="alert">{error}</div>}

          {!loading && visible.nodes.length === 0 && (
            <div className="graph-empty">
              <p className="graph-empty-title">{projectFilter ? t('graph.emptyProject') : t('graph.empty')}</p>
              <p className="graph-empty-sub">{t('graph.emptyHint')}</p>
            </div>
          )}

          <div className="graph-legend" role="group" aria-label={t('graph.legend')}>
            {typesPresent.map((type) => (
              <button
                key={type}
                type="button"
                className={`graph-chip${hiddenTypes.has(type) ? ' graph-chip--off' : ''}`}
                onClick={() => toggleType(type)}
                aria-pressed={!hiddenTypes.has(type)}
              >
                <span className="graph-dot" style={{ background: nodeColor({ type }, isDarkTheme()) }} />
                {t(TYPE_STYLE[type].labelKey)}
                <span className="graph-chip-count">{counts[type]}</span>
              </button>
            ))}
          </div>

          {(unavailable.includes('vault') || pageNotices.length > 0) && (
            <div className="graph-notices">
              {/* Vault locked: a button, not just a sentence (owner) — it
                  unlocks (quick unlock, or the passphrase gate when the vault
                  asks for one) and the graph fills in its hosts/connections. */}
              {unavailable.includes('vault') && (
                <button
                  type="button"
                  className="graph-vault-chip"
                  disabled={unlocking}
                  onClick={(e) => {
                    // Replay the pop on every click (remove, reflow, add).
                    const el = e.currentTarget;
                    el.classList.remove('graph-vault-chip--pop');
                    void el.offsetWidth;
                    el.classList.add('graph-vault-chip--pop');
                    unlockVault();
                  }}
                  onAnimationEnd={(e) => e.currentTarget.classList.remove('graph-vault-chip--pop')}
                >
                  <Lock size={12} aria-hidden />
                  <span>{t('graph.vault.locked')}</span>
                  <span className="graph-vault-chip-action">{t('graph.vault.unlock')}</span>
                </button>
              )}
              {pageNotices.length > 0 && (
                <div className="graph-notice">
                  {pageNotices.map((source) => t(`graph.unavailable.${source}`)).join(' · ')}
                </div>
              )}
            </div>
          )}
        </div>

        {selected && (
          <GraphDetails
            key={selected.id}
            node={selected}
            index={index}
            projects={projects}
            onClose={() => {
              canvasRef.current?.hoverNode(null);
              setSelectedId(null);
            }}
            onPick={(id) => {
              // Owner: clicking a connection selects it — no camera jump.
              canvasRef.current?.hoverNode(null);
              setSelectedId(id);
            }}
            onHoverNode={(id) => canvasRef.current?.hoverNode(id)}
            onRowContextMenu={onContextMenu}
            onOpen={() => openGraphNode(selected, index)}
            onCenter={(id) => center(typeof id === 'string' ? id : selected.id)}
            onStartLink={() => startLink(selected)}
            onAddToProject={(projectId) => withProject(projectId, (p) => addToProject(p, selected))}
            onRemoveFromProject={(projectId) => withProject(projectId, (p) => removeFromProject(p, selected.id))}
            onUnlink={(edge) => run(() => removeLink(edge.id, scopeKeyOfWire(edge.scope)))}
          />
        )}
      </div>

      {menu && menuItems.length > 0 && (
        <ContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={() => setMenu(null)} className="graph-context-menu" />
      )}
      <SshHostChoiceModal />
      {projectPickerFor && (
        <ProjectPickerModal
          node={projectPickerFor}
          projects={projectsFor(projectPickerFor)}
          isMember={(projectId) => memberOfProject(projectPickerFor.id, projectId)}
          onToggle={(projectId) =>
            withProject(projectId, (p) =>
              memberOfProject(projectPickerFor.id, projectId)
                ? removeFromProject(p, projectPickerFor.id)
                : addToProject(p, projectPickerFor)
            )
          }
          onClose={() => setProjectPickerFor(null)}
        />
      )}
      {relationMenu && relationItems.length > 0 && (
        <ContextMenu x={relationMenu.x} y={relationMenu.y} items={relationItems} onClose={() => setRelationMenu(null)} className="graph-context-menu" />
      )}
    </div>
  );
}
